// @ts-check

/**
 * Extracts the component name from the file name.
 *
 * @param {string} fileName
 * @returns {string}
 *
 * @example
 * extractComponentName("SpButton.ce.vue") // "SpButton"
 * extractComponentName("SpTextField.ce.vue") // "SpTextField"
 */
export function extractComponentName(fileName) {
  return fileName.replace(".ce.vue", "");
}

/**
 * Extracts the component file name from a catalog glob path.
 *
 * @param {string} path - e.g. "../components/SpButton/SpButton.ce.vue"
 * @returns {string}
 */
export function componentFileNameFromPath(path) {
  const segments = path.split("/");
  return segments[segments.length - 1];
}

/**
 * Extracts the app directory name from a catalog glob path.
 *
 * @param {string} path - e.g. "../apps/SpFinderOmnisearch/main.js"
 * @returns {string}
 */
export function appDirNameFromPath(path) {
  const segments = path.split("/");
  return segments[segments.length - 2];
}

/**
 * Whether a component opts into form association by declaring `formAssociated: true`.
 *
 * @param {import("vue").Component | import("#ce/framework/mount-ready").ComponentDef} component - The component options object.
 * @returns {boolean}
 */
export function isFormAssociated(component) {
  return Boolean(/** @type {{ formAssociated?: boolean } | undefined} */ (component)?.formAssociated);
}

/**
 * Declares a custom element host as form-associated. Pass to `defineOptions()`
 * inside the `<script setup>` of any host that mounts a form composable
 * (`useForm` / `useFormControl`) — either directly, or through a nested
 * `.ce.vue` piece that has no host element of its own and therefore attaches its
 * `ElementInternals` to this host. Registration reads this flag (via
 * {@link isFormAssociated}) to make the element a form-associated custom element.
 *
 * ```js
 * defineOptions(formAssociatedHost());
 * ```
 *
 * Prefer this over a bare `formAssociated: true`: it names the intent, is
 * greppable, and keeps the `formAssociated` key in one place. The
 * form-association contract test (`framework/form-association.spec.js`) enforces
 * that every host whose boundary uses a form composable declares it.
 *
 * @param {Record<string, unknown>} [options] - Extra component options to merge.
 *   `formAssociated` always wins, so a host cannot accidentally opt back out.
 * @returns {{ formAssociated: true } & Record<string, unknown>}
 */
export function formAssociatedHost(options = {}) {
  return { ...options, formAssociated: true };
}

/**
 * Returns the component's props with their defaults replaced by the deployment defaults
 * (the merged `ui/components.yml` `defaults:` block). A default is looked up per prop, in
 * this order:
 *   - a bare prop name (`densitysmcompact: true`): applies to every component and alias
 *     that declares the prop
 *   - the component's or alias's own entry (`SpCloseButton: { icon: close }`)
 *   - the `global` entry, consulted only for a component without an entry of its own
 *
 * Under the lazy boot the `SYNC_MOUNT_COMPONENTS` (`entry/lazy-boot.js`) define before the
 * configuration arrives and receive no deployment defaults.
 *
 * @param {import("vue").Component} component
 * @param {string} fileName
 * @param {Record<string, unknown>} props
 * @returns {import("vue").ComponentObjectPropsOptions}
 */
export function enrichProps(component, fileName, props) {
  const componentName = extractComponentName(fileName);
  const defaultGlobalProps = /** @type {Record<string, unknown>} */ (props.global ?? {});
  const defaultProps = /** @type {Record<string, unknown>} */ (props[componentName] ?? defaultGlobalProps);

  /**
   * @param {import("vue").ComponentObjectPropsOptions} acc
   * @param {[string, import("vue").Prop<unknown>]} entry
   * @returns {import("vue").ComponentObjectPropsOptions}
   */
  const reducer = (acc, [key, value]) => {
    const defaultPropsValue = props[key] ?? defaultProps[key];

    if (defaultPropsValue !== undefined) {
      value = {
        ...value,
        required: false,
        default: defaultPropsValue,
      };
    }

    return { ...acc, [key]: value };
  };

  const { props: componentProps } = /** @type {import("vue").ComponentOptions} */ (component);

  return Object.entries(componentProps ?? {}).reduce(reducer, {});
}
